import { STORE } from '~/constants/strings'
import { useProjectStore } from '../projects'
import { useProjectDataVisualizationsStore } from './projectDataVisualizations'
import { EnumerationsProportionalAnalyses } from 'client/sratool'

export interface SelectionSnapshot {
  projectID: string | null
  analysisID: string | null
  proportionalAnalysisTypeID: EnumerationsProportionalAnalyses | null
  strataID: string | null
  strataValue: string | null
  groupFieldID: string | null
  selectionVersion: number
}

export const useProjectDataVisualizationSelectionStore = defineStore(
  STORE.PROJECT_DATA_VISUALIZATIONS_SELECTIONS,
  () => {
    const ProjectStore = useProjectStore()
    const ProjectDataVisualizationsStore = useProjectDataVisualizationsStore()

    const selectionVersion = ref(0)

    const projectID = ref<string | null>(null)
    const analysisID = ref<string | null>(null)
    const proportionalAnalysisTypeID =
      ref<EnumerationsProportionalAnalyses | null>(null)
    const strataID = ref<string | null>(null)
    const strataValue = ref<string | null>(null)
    const groupFieldID = ref<string | null>(null)

    const getProjectData = (projectID: string | null) =>
      ProjectStore.projectOptions.find(
        (project) => project.projectID === projectID
      )

    const getAnalysisData = (
      analysisID: string | null,
      proportionalAnalysisTypeID?: EnumerationsProportionalAnalyses | null
    ) => {
      const analysisIDAndProportionalTypeIDMatch =
        ProjectStore.pageSpecificAnalysisOptions.find((analysis) => {
          const analysisIDMatch = analysis.analysisID === analysisID
          const proportionalAnalysisIDMatch = proportionalAnalysisTypeID
            ? analysis.proportionalAnalysisID === proportionalAnalysisTypeID
            : true

          return analysisIDMatch && proportionalAnalysisIDMatch
        })

      return analysisIDAndProportionalTypeIDMatch
    }

    const getStrataData = (strataID: string | null) =>
      ProjectDataVisualizationsStore.strataOptions.find(
        (strata) => strata.strataFieldGUID === strataID
      )

    const isValidOverview = computed(() => {
      return projectID.value !== null
    })

    const isValid = computed(() => {
      return projectID.value !== null && analysisID.value !== null
    })

    const snapshot = () => {
      return {
        projectID: projectID.value,
        analysisID: analysisID.value,
        proportionalAnalysisTypeID: proportionalAnalysisTypeID.value,
        strataID: strataID.value && strataValue.value ? strataID.value : null,
        strataValue:
          strataValue.value && strataID.value ? strataValue.value : null,
        groupFieldID: groupFieldID.value,

        selectionVersion: selectionVersion.value,
      }
    }

    const isCurrent = (snapshot: SelectionSnapshot) => {
      return snapshot.selectionVersion === selectionVersion.value
    }

    const updateSelection = ({
      project,
      analysis,
      proportionalAnalysisType,
      strata,
      strataVal,
      groupField,
    }: {
      project: string | null
      analysis: string | null
      proportionalAnalysisType: EnumerationsProportionalAnalyses | null
      strata: string | null
      strataVal: string | null
      groupField: string | null
    }) => {
      const normalized = {
        project: project ? project : null,
        analysis: analysis ? analysis : null,
        proportionalAnalysisType: proportionalAnalysisType
          ? proportionalAnalysisType
          : null,
        strata: strata ? strata : null,
        strataVal: strataVal ? strataVal : null,
        groupField: groupField ? groupField : null,
      }
      const noChange =
        projectID.value === normalized.project &&
        analysisID.value === normalized.analysis &&
        proportionalAnalysisTypeID.value === proportionalAnalysisType &&
        strataID.value === normalized.strata &&
        strataValue.value === normalized.strataVal &&
        groupFieldID.value === normalized.groupField

      if (noChange) {
        return
      }

      if (projectID.value !== normalized.project) {
        projectID.value = normalized.project
        analysisID.value = null
        strataID.value = null
        strataValue.value = null
        groupFieldID.value = null

        selectionVersion.value++
        return
      }

      if (
        analysisID.value !== normalized.analysis ||
        proportionalAnalysisTypeID.value !== normalized.proportionalAnalysisType
      ) {
        analysisID.value = normalized.analysis
        proportionalAnalysisTypeID.value = normalized.proportionalAnalysisType
        strataID.value = null
        strataValue.value = null
        groupFieldID.value = null

        selectionVersion.value++
        return
      }

      if (strataID.value !== normalized.strata) {
        const strataValueChange = strataValue.value !== null
        strataID.value = normalized.strata
        strataValue.value = null

        if (normalized.strata === null || strataValueChange) {
          selectionVersion.value++
        }

        return
      }

      if (strataValue.value !== normalized.strataVal) {
        strataValue.value = normalized.strataVal

        selectionVersion.value++

        return
      }

      if (groupFieldID.value !== normalized.groupField) {
        groupFieldID.value = normalized.groupField

        selectionVersion.value++
        return
      }
    }

    return {
      projectID,
      analysisID,
      proportionalAnalysisTypeID,
      strataID,
      strataValue,
      groupFieldID,

      selectionVersion,

      getProjectData,
      getAnalysisData,
      getStrataData,

      isValidOverview,
      isValid,
      snapshot,
      isCurrent,
      updateSelection,
    }
  },
  {
    persist: {
      omit: ['selectionVersion'],
    },
  }
)
